import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

import type { MeUser } from "./auth";

export type AdminUser = MeUser;

export type Role = "admin" | "user";

export interface AdminUserCreateRequest {
  email: string;
  display_name: string | null;
  role: Role;
  password?: string;
  generate?: boolean;
}

export interface AdminUserCreateResponse {
  user: AdminUser;
  generated_password: string | null;
}

export interface AdminUserUpdateRequest {
  display_name?: string | null;
  role?: Role;
  is_active?: boolean;
}

export interface AdminPasswordResetRequest {
  password?: string;
  generate?: boolean;
}

export interface AdminPasswordResetResponse {
  generated_password: string | null;
}

export const adminUsersListKey = ["admin", "users"] as const;
export const adminUserKey = (id: string) => ["admin", "user", id] as const;

const LIST_LIMIT = 200;

export function useAdminUsersList() {
  return useQuery<AdminUser[]>({
    queryKey: adminUsersListKey,
    queryFn: () => apiFetch<AdminUser[]>(`/admin/users?limit=${LIST_LIMIT}`),
    staleTime: 30_000,
  });
}

export function useAdminUser(id: string | undefined) {
  return useQuery<AdminUser>({
    queryKey: id ? adminUserKey(id) : ["admin", "user", "__none__"],
    queryFn: () => apiFetch<AdminUser>(`/admin/users/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserCreateRequest) =>
      apiFetch<AdminUserCreateResponse>("/admin/users", {
        method: "POST",
        body,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: adminUsersListKey });
    },
  });
}

export function useUpdateAdminUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserUpdateRequest) =>
      apiFetch<AdminUser>(`/admin/users/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: async (updated) => {
      qc.setQueryData(adminUserKey(id), updated);
      await qc.invalidateQueries({ queryKey: adminUsersListKey });
    },
  });
}

export function useResetAdminPassword(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminPasswordResetRequest) =>
      apiFetch<AdminPasswordResetResponse>(`/admin/users/${id}/reset-password`, {
        method: "POST",
        body,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: adminUserKey(id) });
    },
  });
}

export function useDeactivateAdminUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<AdminUser>(`/admin/users/${id}/deactivate`, { method: "POST" }),
    onSuccess: async (updated) => {
      qc.setQueryData(adminUserKey(id), updated);
      await qc.invalidateQueries({ queryKey: adminUsersListKey });
    },
  });
}

export function useDeleteAdminUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      qc.removeQueries({ queryKey: adminUserKey(id) });
      await qc.invalidateQueries({ queryKey: adminUsersListKey });
    },
  });
}
